import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, FileSpreadsheet, Pencil, Trash2, ClipboardList, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, downloadFile } from "@/lib/api";
import { inr, dmy, DUTY_STATUSES } from "@/lib/format";
import { PageHeader, Panel, StatusBadge, EmptyState, ConfirmDelete, MobileCards, MobileCard } from "@/components/common";
import { MobileFab } from "@/components/MobileFab";
import { useAuth } from "@/context/AuthContext";

const ALL = "all";

function FilterSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className="h-11 sm:h-10 w-full sm:w-44" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-popover">
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function Duties() {
  const nav = useNavigate();
  const admin = useAuth().user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [f, setF] = useState({ q: "", company_id: "", status: "", source: "", date_from: "", date_to: "" });
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/duties", { params: f }).then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [f]); // eslint-disable-line

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const remove = async (id) => {
    try { await api.delete(`/duties/${id}`); toast.success("Duty deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const exportMis = async (internal) => {
    try { await downloadFile("/duties-export/xlsx", internal ? "duty_internal_report.xlsx" : "duty_mis.xlsx", { ...f, internal }); }
    catch (e) { toast.error("Export failed"); }
  };

  const tot = rows.reduce((a, r) => ({ b: a.b + (r.billing_total || 0), c: a.c + (r.total_cost || 0), p: a.p + (r.profit || 0), k: a.k + (r.total_km || 0) }), { b: 0, c: 0, p: 0, k: 0 });
  const hasFilter = Object.values(f).some(Boolean);

  return (
    <div data-testid="duties-page">
      <PageHeader eyebrow="Operations" title="Duty Register" subtitle={admin ? "All corporate duties with KMs, billing and profit." : "All corporate duties with KMs and billing."}
        actions={<>
          {admin && <><Button variant="outline" onClick={() => exportMis(false)} className="h-11 sm:h-10" data-testid="export-mis-btn"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> MIS Excel</Button>
          <Button variant="outline" onClick={() => exportMis(true)} className="h-11 sm:h-10" data-testid="export-internal-btn"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Internal Report</Button></>}
          <Button asChild className="h-11 sm:h-10" data-testid="new-duty-btn"><Link to="/duties/new"><Plus className="h-4 w-4 mr-1.5" /> New Duty</Link></Button>
        </>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[["Duties", rows.length, "sum-count"], ["Billing", inr(tot.b), "sum-billing"], ...(admin ? [["Cost", inr(tot.c), "sum-cost"], ["Profit", inr(tot.p), "sum-profit"]] : [])].map(([l, v, t]) => (
          <div key={l} className="rounded-lg border bg-card px-4 py-3" data-testid={t}>
            <div className="eyebrow">{l}</div>
            <div className={`font-heading font-bold text-lg sm:text-xl num mt-1 break-words ${l === "Profit" ? (tot.p >= 0 ? "text-emerald-600" : "text-rose-600") : ""}`}>{v}</div>
          </div>
        ))}
      </div>

      <Panel>
        <div className="grid grid-cols-2 sm:flex sm:flex-row sm:flex-wrap gap-3 mb-4 [&>*:first-child]:col-span-2">
          <div className="relative sm:w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Duty no, guest, vehicle..." className="pl-9 h-11 sm:h-10" data-testid="duty-search-input" />
          </div>
          <FilterSelect value={f.company_id} onChange={(v) => set("company_id", v)} placeholder="All companies" testid="filter-company"
            options={companies.map((c) => ({ value: c.id, label: c.name }))} />
          <FilterSelect value={f.status} onChange={(v) => set("status", v)} placeholder="All statuses" testid="filter-status"
            options={DUTY_STATUSES.map((s) => ({ value: s, label: s }))} />
          <FilterSelect value={f.source} onChange={(v) => set("source", v)} placeholder="Own + Market" testid="filter-source"
            options={[{ value: "own", label: "Own vehicles" }, { value: "market", label: "Market vehicles" }]} />
          <Input type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} aria-label="From date" className="h-11 sm:h-10 w-full sm:w-40" data-testid="filter-date-from" />
          <Input type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} aria-label="To date" className="h-11 sm:h-10 w-full sm:w-40" data-testid="filter-date-to" />
          {hasFilter && <Button variant="ghost" onClick={() => setF({ q: "", company_id: "", status: "", source: "", date_from: "", date_to: "" })} data-testid="clear-filters-btn"><X className="h-4 w-4 mr-1" /> Clear</Button>}
        </div>

        {!loading && rows.length === 0 ? (
          <EmptyState icon={ClipboardList} title={hasFilter ? "No duties match these filters" : "No duties yet"} text="Duties you create will appear here."
            action={<Button asChild><Link to="/duties/new"><Plus className="h-4 w-4 mr-1" /> New Duty</Link></Button>} />
        ) : (<>
          <MobileCards items={rows} testid="duties-mobile-list" render={(r) => (
            <MobileCard key={r.id} testid={`duty-card-${r.id}`} onClick={() => nav(`/duties/${r.id}`)}
              title={<span>{r.company_name} <span className="font-mono text-xs text-muted-foreground">{r.duty_no}</span></span>}
              subtitle={`${dmy(r.duty_date)} · ${r.guest_name || "-"}${r.pickup_location ? ` · ${r.pickup_location}${r.drop_location ? ` → ${r.drop_location}` : ""}` : ""}`}
              right={<StatusBadge value={r.status} />}
              rows={[["Vehicle", <span className="font-mono text-xs">{r.vehicle_number || "-"} <StatusBadge value={r.vehicle_source} /></span>], ["KM / Hrs", <span className="num">{r.total_km} km · {r.total_hours} h</span>],
                ["Billing", <span className="num font-semibold">{inr(r.billing_total)}</span>], admin && ["Profit", <span className={`num font-semibold ${r.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{inr(r.profit)}</span>]]}
              actions={<>
                <Button size="sm" variant="outline" className="h-10 flex-1" asChild><Link to={`/duties/${r.id}`} data-testid={`duty-card-edit-${r.id}`}><Pencil className="h-4 w-4 mr-1" /> Edit</Link></Button>
                <ConfirmDelete onConfirm={() => remove(r.id)} trigger={<Button size="sm" variant="outline" className="h-10 text-rose-600" data-testid={`duty-card-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>} />
              </>} />
          )} />
          <div className="hidden md:block overflow-x-auto -mx-4 sm:-mx-5">
            <table className="w-full text-sm" data-testid="duties-table">
              <thead>
                <tr className="border-b text-left">
                  {["Duty", "Date", "Company", "Guest / Route", "Vehicle", "KM / Hrs", "Billing", ...(admin ? ["Cost", "Profit"] : []), "Status", ""].map((h) => (
                    <th key={h} className={`px-4 py-2.5 eyebrow whitespace-nowrap ${["Billing", "Cost", "Profit"].includes(h) ? "text-right" : ""}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => nav(`/duties/${r.id}`)} data-testid={`duty-row-${r.id}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold whitespace-nowrap">{r.duty_no}<div className="text-muted-foreground font-sans font-normal">{r.booking_ref}</div></td>
                    <td className="px-4 py-3 whitespace-nowrap">{dmy(r.duty_date)}</td>
                    <td className="px-4 py-3 font-medium">{r.company_name}</td>
                    <td className="px-4 py-3 max-w-[220px]"><div className="truncate">{r.guest_name || "-"}</div><div className="text-xs text-muted-foreground truncate">{r.pickup_location} {r.drop_location ? `→ ${r.drop_location}` : ""}</div></td>
                    <td className="px-4 py-3 whitespace-nowrap"><div className="font-mono text-xs">{r.vehicle_number || "-"}</div><StatusBadge value={r.vehicle_source} /></td>
                    <td className="px-4 py-3 num text-xs whitespace-nowrap">{r.total_km} km<div className="text-muted-foreground">{r.total_hours} hrs</div></td>
                    <td className="px-4 py-3 text-right num font-semibold">{inr(r.billing_total)}</td>
                    {admin && <td className="px-4 py-3 text-right num text-muted-foreground">{inr(r.total_cost)}</td>}
                    {admin && <td className={`px-4 py-3 text-right num font-semibold ${r.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{inr(r.profit)}</td>}
                    <td className="px-4 py-3"><StatusBadge value={r.status} />{r.invoice_no && <div className="text-[10px] font-mono text-muted-foreground mt-1">{r.invoice_no}</div>}</td>
                    <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" asChild data-testid={`duty-edit-${r.id}`}><Link to={`/duties/${r.id}`}><Pencil className="h-4 w-4" /></Link></Button>
                      <ConfirmDelete onConfirm={() => remove(r.id)} trigger={<Button variant="ghost" size="icon" className="text-rose-600" data-testid={`duty-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>)}
      </Panel>
      <div className="h-16 lg:hidden" />
      <MobileFab to="/duties/new" label="New Duty" testid="duties-fab-new-duty" />
    </div>
  );
}
